import { useState } from 'react';
import { z } from 'zod';
import { Link, useNavigate } from 'react-router';

// hooks
import useAuth from '@hooks/use-auth';

// UI
import AuthInput from '@components/auth-input';
import Button from '@components/button';
import BackButton from '@components/back-button';
import AppleSignInButton from '@components/apple-signin-button';

// images
import LogoImg from '@assets/logo.png';
import GoogleIcon from '@assets/icons/google_ic.svg?react';

// styles
import css from './styles.module.css';

interface LoginFormInputs {
  email: string;
  password: string;
}

const loginSchema = z.object({
  email: z.string().email({ message: 'Invalid email format' }),
  password: z.string().min(6, {
    message: 'Password must be 5 or more characters',
  }),
});

const URL = '/api/auth/login';

function Login() {
  const navigate = useNavigate();
  const { checkAuth } = useAuth();

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<LoginFormInputs>({
    email: '',
    password: '',
  });
  const [errors, setErrors] = useState<
    Partial<Record<keyof LoginFormInputs, string>>
  >({});

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setFormData((prev) => ({ ...prev, [name]: value }));

    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    try {
      loginSchema.parse(formData);

      setLoading(true);

      const response = await fetch(URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (data.status === 'error') {
        setLoading(false);
        setErrors((prev) => ({ ...prev, ...data }));
      } else if (data.status === 'success') {
        const userData = await checkAuth();

        setLoading(false);

        if (userData && userData.total_cards === 0) {
          /**
           * Redirect user to onboarding if they don't have any cards
           */

          navigate('/main');
          return;
        }

        navigate('/account');
      } else if (data.status === 'premaid') {
        navigate(`/change-password?token=${encodeURIComponent(data.token)}`);
      }
    } catch (error) {
      if (error instanceof z.ZodError) {
        const fieldErrors: Partial<Record<keyof LoginFormInputs, string>> = {};
        error.errors.forEach((err) => {
          const field = err.path[0] as keyof LoginFormInputs;
          fieldErrors[field] = err.message;
        });
        setErrors(fieldErrors);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleClick = () => {
    window.location.href = '/login_google';
  };

  return (
    <div className={css.loginRoot}>
      <div className={css.contentWrapper}>
        <div>
          <img src={LogoImg} alt="Logo" className={css.logoImg} />

          <BackButton className={css.backButton} />

          <h3 className={css.title}>Welcome back!</h3>

          <p className={css.subtitle}>
            Login to create loyalty cards and send notifications
          </p>

          <form noValidate className={css.formWrapper} onSubmit={handleSubmit}>
            <div className={css.inputFields}>
              <AuthInput
                name="email"
                value={formData.email}
                onChange={handleChange}
                type="email"
                placeholder="Email"
                error={errors.email}
                className={css.emailInput}
              />

              <AuthInput
                name="password"
                value={formData.password}
                onChange={handleChange}
                type="password"
                error={errors.password}
                placeholder="Password"
              />
            </div>

            <Link to="/forgot-password" className={css.forgotLink}>
              Forgot password?
            </Link>

            <Button
              loading={loading}
              disabled={loading}
              type="submit"
              size="large"
              className={css.loginButton}
            >
              Login
            </Button>
          </form>

          <p className={css.signUpText}>
            Don't have an account?{' '}
            <Link to="/sign-up" className={css.signUpLink}>
              Sign Up
            </Link>
          </p>
        </div>

        <footer className={css.formFooter}>
          <div className={css.divider}>
            <span className={css.dividerText}>Or</span>
          </div>

          <div className={css.oauthControls}>
            <AppleSignInButton
              clientId="turbopushoauth"
              redirectURI="https://turbopush.io/auth_apple"
              className={css.oauthBtn}
            />
            <Button
              size="large"
              color="grey"
              onClick={handleGoogleClick}
              className={css.oauthBtn}
            >
              <>
                <GoogleIcon className={css.oauthIcon} width={24} height={24} />
              </>
              Login with Google
            </Button>
          </div>

          <p className={css.footerText}>
            By continuing, you agree to Turbopush’s{' '}
            <a href="/terms" className={css.link}>
              Terms of Servise
            </a>{' '}
            and{' '}
            <a href="/privacy-policy" className={css.link}>
              Privacy Policy
            </a>
          </p>
        </footer>
      </div>
    </div>
  );
}

export default Login;
